import { useMemo, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InboxIcon from '@mui/icons-material/Inbox';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { batteryModelsApi } from '@/api/modules/batteryModels';
import { customersApi } from '@/api/modules/customers';
import { quotationsApi } from '@/api/modules/quotations';
import { salesOrdersApi } from '@/api/modules/salesOrders';
import { workflowApi, type TaskView } from '@/api/modules/workflow';
import { paths } from '@/router/paths';

/**
 * Post-login landing page.
 *
 * Structure:
 *   1. Greeting — time-of-day + displayName
 *   2. Pending actions — workflow tasks assigned to the current user
 *   3. Stats — counts (battery models, my task count). New sources plug in
 *      as their API clients ship (quotations + sales orders in Wave 1).
 *   4. Shortcuts — quick-nav cards to core modules.
 *
 * Every section degrades gracefully: 401/403/network failures render a
 * benign empty/error state, they never blank out the whole page.
 */
export function DashboardPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const greeting = useMemo(() => timeOfDayGreeting(), []);
  const displayName = session?.displayName ?? 'there';

  // --- Data ---------------------------------------------------------------

  const customersQuery = useQuery({
    queryKey: ['dashboard', 'customers', 'total'],
    queryFn: () => customersApi.summaries(0, 1)
  });

  const batteryModelsQuery = useQuery({
    // A 1-item summaries call is the cheapest way to read totalItems.
    queryKey: ['dashboard', 'battery-models', 'total'],
    queryFn: () => batteryModelsApi.summaries(0, 1)
  });

  const quotationsQuery = useQuery({
    queryKey: ['dashboard', 'quotations', 'total'],
    queryFn: () => quotationsApi.summaries(0, 1)
  });

  const salesOrdersQuery = useQuery({
    queryKey: ['dashboard', 'sales-orders', 'total'],
    queryFn: () => salesOrdersApi.summaries(0, 1)
  });

  const myTasksQuery = useQuery({
    queryKey: ['dashboard', 'my-tasks', session?.subject],
    queryFn: () => workflowApi.tasksForAssignee(session!.subject),
    enabled: Boolean(session?.subject)
  });

  // --- Render -------------------------------------------------------------

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        {greeting}, {displayName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Here's what needs your attention today.
      </Typography>

      <Section title="Pending actions">
        <PendingActions
          isLoading={myTasksQuery.isLoading}
          error={myTasksQuery.error}
          tasks={myTasksQuery.data ?? []}
          onOpenTask={(task) => navigate(`${paths.workflowInbox}?taskId=${task.id}`)}
          onSeeAll={() => navigate(paths.workflowInbox)}
        />
      </Section>

      <Section title="At a glance">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' },
            gap: 2
          }}
        >
          <StatCard
            label="Customers"
            value={customersQuery.data?.totalItems}
            isLoading={customersQuery.isLoading}
            onClick={() => navigate(paths.customers)}
          />
          <StatCard
            label="Battery models"
            value={batteryModelsQuery.data?.totalItems}
            isLoading={batteryModelsQuery.isLoading}
            onClick={() => navigate(paths.batteryModels)}
          />
          <StatCard
            label="Quotations"
            value={quotationsQuery.data?.totalItems}
            isLoading={quotationsQuery.isLoading}
            onClick={() => navigate(paths.quotations)}
          />
          <StatCard
            label="Sales orders"
            value={salesOrdersQuery.data?.totalItems}
            isLoading={salesOrdersQuery.isLoading}
            onClick={() => navigate(paths.salesOrders)}
          />
          <StatCard
            label="My tasks"
            value={myTasksQuery.data?.length}
            isLoading={myTasksQuery.isLoading}
            onClick={() => navigate(paths.workflowInbox)}
          />
        </Box>
      </Section>

      <Section title="Shortcuts">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 2
          }}
        >
          {shortcuts.map((s) => (
            <ShortcutCard key={s.path} {...s} onClick={() => navigate(s.path)} />
          ))}
        </Box>
      </Section>
    </Box>
  );
}

// --- Local building blocks ------------------------------------------------

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h2" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

interface PendingActionsProps {
  isLoading: boolean;
  error: unknown;
  tasks: TaskView[];
  onOpenTask: (task: TaskView) => void;
  onSeeAll: () => void;
}

function PendingActions({ isLoading, error, tasks, onOpenTask, onSeeAll }: PendingActionsProps) {
  if (isLoading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading your tasks…
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ '& .MuiAlert-message': { flex: 1 } }}>
        Couldn't load your task inbox right now. Try{' '}
        <a href="#" onClick={(e) => { e.preventDefault(); onSeeAll(); }}>opening it directly</a>.
      </Alert>
    );
  }

  if (tasks.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            You're all caught up — no workflow tasks are assigned to you.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const top = tasks.slice(0, 5);
  const remaining = tasks.length - top.length;

  return (
    <Card variant="outlined">
      <List disablePadding>
        {top.map((task, idx) => (
          <Box key={task.id}>
            {idx > 0 && <Divider component="li" />}
            <ListItem disablePadding>
              <ListItemButton onClick={() => onOpenTask(task)}>
                <ListItemText
                  primary={task.name}
                  secondary={
                    <>
                      <Chip
                        size="small"
                        label={task.processDefinitionKey}
                        sx={{ mr: 1 }}
                      />
                      {task.dueDate ? `Due ${formatDate(task.dueDate)}` : 'No due date'}
                    </>
                  }
                  secondaryTypographyProps={{ component: 'span' }}
                />
                <ArrowForwardIcon fontSize="small" color="action" />
              </ListItemButton>
            </ListItem>
          </Box>
        ))}
        {remaining > 0 && (
          <>
            <Divider component="li" />
            <ListItem disablePadding>
              <ListItemButton onClick={onSeeAll}>
                <ListItemText
                  primary={`+ ${remaining} more`}
                  primaryTypographyProps={{ color: 'primary', variant: 'body2' }}
                />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Card>
  );
}

interface StatCardProps {
  label: string;
  value: number | undefined;
  isLoading?: boolean;
  muted?: string;
  onClick?: () => void;
}

function StatCard({ label, value, isLoading, muted, onClick }: StatCardProps) {
  const inner = (
    <CardContent>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width={60} height={40} />
      ) : muted ? (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          {muted}
        </Typography>
      ) : (
        <Typography variant="h3" sx={{ fontSize: '1.75rem', fontWeight: 700 }}>
          {value ?? '—'}
        </Typography>
      )}
    </CardContent>
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      {onClick ? <CardActionArea onClick={onClick}>{inner}</CardActionArea> : inner}
    </Card>
  );
}

interface Shortcut {
  label: string;
  path: string;
  icon: ReactNode;
}

const shortcuts: Shortcut[] = [
  { label: 'Customers', path: paths.customers, icon: <PeopleAltIcon /> },
  { label: 'Battery models', path: paths.batteryModels, icon: <BatteryChargingFullIcon /> },
  { label: 'Quotations', path: paths.quotations, icon: <RequestQuoteIcon /> },
  { label: 'Sales orders', path: paths.salesOrders, icon: <ShoppingCartIcon /> },
  { label: 'Workflow inbox', path: paths.workflowInbox, icon: <InboxIcon /> },
  { label: 'Dynamic records', path: paths.dynamicRecords, icon: <DynamicFormIcon /> },
  { label: 'Registry admin', path: paths.registryAdmin, icon: <QrCode2Icon /> }
];

function ShortcutCard({ label, icon, onClick }: Shortcut & { onClick: () => void }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ color: 'primary.main' }}>{icon}</Box>
            <Typography variant="body1">{label}</Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// --- Utilities ------------------------------------------------------------

function timeOfDayGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return iso;
  }
}
